<script>
/**
 * TriggerSuggesterModal — wizard exibido apos um lead ser arrastado para a
 * coluna "Won/Ganho" do funil CRM.
 *
 * Fluxos (REQUIREMENTS §6.4 + UC-T02 / UC-T03):
 *   - mappings.length === 1 → pula o picker e vai direto ao step 'wizard'
 *     com o template ja selecionado e o form pre-preenchido.
 *   - mappings.length >= 2  → step 'picker' com radio-list dos templates;
 *     ao confirmar passa para 'wizard'.
 *
 * Interpolacao: o `title` do template e interpolado via triggerInterpolator
 * usando o `vars` do payload — variaveis ausentes viram string vazia
 * (AC-12 / AC-13).
 *
 * Persistencia: reusa o endpoint v1 `/templates/<id>/instantiate` via
 * `apiTarefas.instantiateTemplate`. Nenhum branch novo no backend.
 *
 * "Dispensar" / "Cancelar": fecha o modal SEM nenhuma persistencia
 * (REQUIREMENTS §11.3 / AC-17). O drag em si permanece efetivado no CRM.
 */
import Vue from 'vue'
import moment from 'moment'
import { GlobalBus } from '@services/bus'
import { MODAL } from '@constants/Text'
import { interpolate } from '@modules/tarefas/services/triggerInterpolator'
import TaskAssigneePicker from '@modules/tarefas/components/task-assignee-picker/task-assignee-picker.vue'
import TaskClientPicker from '@modules/tarefas/components/task-client-picker/task-client-picker.vue'
import { MIN_LOADING_MS } from '@modules/tarefas/constants/timing'
import Title from '@components/section-title'

// Lazy-loaded inside submit() para evitar puxar `apiTarefas` (e sua cadeia de
// imports, incluindo o connection global) no eager-require de `_404.vue` →
// `@layouts/main` → este modal. Em runtime, este require resolve sincronamente
// na primeira chamada (cache de modulos do CommonJS).
function _apiTarefas() {
  return require('@modules/tarefas/api/apiTarefas').apiTarefas
}

function emptyForm() {
  return {
    title: '',
    responsavelPrincipal: [],
    participantes: [],
    cliente: null,
    baseDate: '',
    sprintId: null,
    folderId: null,
  }
}

function pickIdTipo(person) {
  if (!person) return null
  return { id: person.id, tipo: person.tipo || 'profissional' }
}

// InputByType type="date" expects and emits DD/MM/YYYY (masked field).
function todayDisplay() {
  return moment().format('DD/MM/YYYY')
}

function displayDateToIso(ddmmyyyy) {
  const parsed = moment(ddmmyyyy, 'DD/MM/YYYY', true)
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null
}

export default {
  name: 'TriggerSuggesterModal',
  components: { TaskAssigneePicker, TaskClientPicker, Title },
  data() {
    return {
      busModal: new Vue(),
      loading: false,
      loadingFolders: false,
      onCloseCallback: null,
      step: 'wizard',
      mappings: [],
      vars: {},
      selectedMappingId: null,
      form: emptyForm(),
    }
  },
  computed: {
    // Lista ACHATADA (raiz + subpastas). `state.tarefas.folders` guarda só as
    // raízes, então ler o state direto esconderia do seletor toda pasta
    // aninhada — o trigger não poderia apontar para uma lista de subpasta.
    folders() {
      return this.$store.getters['tarefas/allFolders'] || []
    },
    folderOptions() {
      return this.folders.map((f) => ({ text: f.name || f.nome, value: f.id }))
    },
    sprintOptions() {
      if (!this.form.folderId) return []
      const folder = this.folders.find((f) => f.id === this.form.folderId)
      if (!folder || !Array.isArray(folder.sprints)) return []
      return folder.sprints.map((s) => ({ text: s.name || s.nome, value: s.id }))
    },
    selectedMapping() {
      if (!this.selectedMappingId) return null
      return this.mappings.find((m) => m.id === this.selectedMappingId) || null
    },
    pickerOptions() {
      return (this.mappings || []).map(function (m) {
        return { id: m.id, title: m.templateTitle || '—' }
      })
    },
    modalTitle() {
      if (this.step === 'picker') return 'Qual template usar?'
      if (this.selectedMapping) {
        return 'Criar task: ' + (this.selectedMapping.templateTitle || '')
      }
      return 'Criar task a partir de template'
    },
    canContinueFromPicker() {
      return !!this.selectedMappingId
    },
    canSubmit() {
      return !this.loading && !!this.selectedMapping && !!this.selectedMapping.idTemplate && !!this.form.sprintId
    },
    isGestor() {
      const user = this.$currentUser ? this.$currentUser() : null
      return !!(user && user.IS_GESTOR)
    },
  },
  watch: {
    'form.folderId': function (newFolderId, oldFolderId) {
      if (newFolderId !== oldFolderId) {
        const stillValid = this.sprintOptions.some((s) => s.value === this.form.sprintId)
        if (!stillValid) this.form.sprintId = null
      }
    },
  },
  created() {
    GlobalBus.$on(MODAL.PM_TRIGGER_SUGGESTER, this.open)
  },
  beforeDestroy() {
    GlobalBus.$off(MODAL.PM_TRIGGER_SUGGESTER, this.open)
  },
  methods: {
    clear() {
      this.loading = false
      this.loadingFolders = false
      this.onCloseCallback = null
      this.step = 'wizard'
      this.mappings = []
      this.vars = {}
      this.selectedMappingId = null
      this.form = emptyForm()
    },
    dismiss() {
      if (this.onCloseCallback) this.onCloseCallback()
      this.busModal.$emit('dialog', false)
    },
    open(payload) {
      this.clear()
      const data = payload || {}
      this.mappings = Array.isArray(data.mappings) ? data.mappings : []
      this.vars = data.vars || {}
      this.onCloseCallback = typeof data.onClose === 'function' ? data.onClose : null
      this._resolveInitialStep()
      this.busModal.$emit('dialog', true)
      // O empty-state não precisa de folders/assignees; só carrega on-demand
      // quando o fluxo vai de fato para picker/wizard.
      if (this.step !== 'empty') this._loadContextOnDemand()
    },
    _resolveInitialStep() {
      if (this.mappings.length === 0) {
        // No mappings configured — show onboarding empty state so the user
        // learns how to activate the feature.
        this.step = 'empty'
        return
      }
      if (this.mappings.length === 1) {
        this.selectedMappingId = this.mappings[0].id
        this.step = 'wizard'
        this._populateWizardFromSelection()
        return
      }
      this.step = 'picker'
    },
    _loadContextOnDemand() {
      // Carrega folders e assignees on-demand se o usuário está vindo do CRM
      // (módulo tarefas ainda não foi visitado nesta sessão). O modal já foi
      // aberto; os seletores ficam desabilitados enquanto carregam.
      if (!this.folders.length) {
        this.loadingFolders = true
        this.$store.dispatch('tarefas/loadFolders')
          .catch(function () {})
          .finally(() => { this.loadingFolders = false })
      }
      if (this.$store.getters['tarefas/assigneesProfessionals'].length) {
        this._enrichClientFromStore()
      } else {
        this.$store.dispatch('tarefas/loadAssignees')
          .then(() => { this._enrichClientFromStore() })
          .catch(function () {})
      }
    },
    confirmPicker() {
      if (!this.selectedMappingId) return
      this.step = 'wizard'
      this._populateWizardFromSelection()
    },
    _getOwnerId() {
      return (this.vars && this.vars.owner && this.vars.owner.id) ||
        (this.vars && this.vars.lead && this.vars.lead.ownerIdProfissional) || null
    },
    _getOwnerNome() {
      return (this.vars && this.vars.owner && this.vars.owner.nome) || ''
    },
    _getClientId() {
      return (this.vars && this.vars.lead && this.vars.lead.idClienteApp) || null
    },
    _findFolderForSprint(sprintId) {
      return this.folders.find(function (f) {
        return Array.isArray(f.sprints) && f.sprints.some(function (s) { return s.id === sprintId })
      }) || null
    },
    _populateWizardFromSelection() {
      const mapping = this.selectedMapping
      if (!mapping) return
      const ownerId = this._getOwnerId()
      const ownerNome = this._getOwnerNome()
      const clientId = this._getClientId()
      this.form.title = interpolate(mapping.templateTitle || '', this.vars) || ''
      this.form.responsavelPrincipal = ownerId
        ? [{ id: ownerId, tipo: 'profissional', nome: ownerNome }]
        : []
      this.form.participantes = []
      this.form.cliente = clientId ? { id: clientId } : null
      this.form.baseDate = todayDisplay()
      this._applyDefaultSprint(mapping)
    },
    // Pre-fill sprint from template default so CRM Won doesn't need manual pick.
    // Resolve o folder dono do sprint default; ambos ficam null se não houver.
    _applyDefaultSprint(mapping) {
      const idSprintDefault = mapping.idSprintDefault || null
      this.form.sprintId = idSprintDefault
      if (idSprintDefault) {
        const folder = this._findFolderForSprint(idSprintDefault)
        this.form.folderId = folder ? folder.id : null
      } else {
        this.form.folderId = null
      }
    },
    _enrichClientFromStore() {
      // Enrich responsavelPrincipal with avatarUrl from store (name already set from vars)
      const professionals = this.$store.getters['tarefas/assigneesProfessionals']
      this.form.responsavelPrincipal = this.form.responsavelPrincipal.map(function (p) {
        const found = professionals.find(function (s) { return s.id === p.id })
        return found ? { id: found.id, tipo: 'profissional', nome: found.nome, avatarUrl: found.avatarUrl || found.avatar_url || null } : p
      })
      // Enrich cliente with nome + avatarUrl from store
      if (!this.form.cliente || this.form.cliente.id == null) return
      const clients = this.$store.getters['tarefas/assigneesClients']
      const found = clients.find((c) => c.id === this.form.cliente.id)
      if (found) this.form.cliente = { id: found.id, nome: found.nome, avatarUrl: found.avatarUrl || found.avatar_url || null }
    },
    onResponsavelInput(val) {
      this.form.responsavelPrincipal = Array.isArray(val) ? val : []
    },
    onParticipantesInput(val) {
      this.form.participantes = Array.isArray(val) ? val : []
    },
    onClienteInput(val) {
      this.form.cliente = val
    },
    _buildPayload() {
      const responsavel = this.form.responsavelPrincipal[0] || null
      const payload = {
        title: (this.form.title || '').trim() || null,
        responsavelPrincipal: pickIdTipo(responsavel),
        participantes: (this.form.participantes || []).map(pickIdTipo).filter(Boolean),
      }
      if (this.form.cliente && this.form.cliente.id != null) { // NOSONAR
        payload.clienteAppId = this.form.cliente.id
      }
      if (this.form.baseDate) {
        const isoDate = displayDateToIso(this.form.baseDate)
        if (isoDate) payload.baseDate = isoDate
      }
      if (this.form.sprintId) payload.sprintId = this.form.sprintId
      if (this.form.folderId) payload.folderId = this.form.folderId
      return payload
    },
    _releaseLoading(startedAt) {
      const elapsed = Date.now() - startedAt
      const wait = elapsed < MIN_LOADING_MS ? MIN_LOADING_MS - elapsed : 0
      setTimeout(() => { this.loading = false }, wait)
    },
    _handleSuccess(r) {
      this.$emit('instantiated', r.value)
      // Refresca o board (mesma estratégia do template-instantiate-modal).
      if (this.$store && this.$store.dispatch) {
        this.$store.dispatch('tarefas/loadTasks')
      }
      const onClose = this.onCloseCallback
      this.busModal.$emit('dialog', false)
      if (onClose) onClose()
    },
    goToTriggerSettings() {
      this.busModal.$emit('dialog', false)
      this.$router.push({ name: 'task.configuracoes.automatizacoes' }).catch(function () {})
    },
    async submit() {
      if (this.loading) return
      if (!this.selectedMapping || !this.selectedMapping.idTemplate) {
        this.$showSnack({
          status: 400,
          message: 'Selecione um template antes de continuar.',
        })
        return
      }
      this.loading = true
      const startedAt = Date.now()
      let r
      try {
        r = await _apiTarefas().instantiateTemplate({
          templateId: this.selectedMapping.idTemplate,
          payload: this._buildPayload(),
        })
      } finally {
        this._releaseLoading(startedAt)
      }
      this.$showSnack(r)
      if (r && r.status === 200) this._handleSuccess(r)
    },
  },
}
</script>

<template>
  <BaseModal :bus="busModal" centerScreen maxWidth="640px">
    <template v-slot:title>
      <Title :title="modalTitle" underlined class="mb-4" />
    </template>

    <div class="trigger-suggester-modal pa-6">
      <!-- Empty step — no mappings configured -->
      <div v-if="step === 'empty'">
        <div v-if="isGestor" class="text-center py-4">
          <icon-svg name="mdi-lightning-bolt-outline" :size="48" color="var(--secondary)" class="mb-3" />
          <p class="az-text-body text-color-700 mb-2">
            Nenhum template de task configurado para leads ganhos.
          </p>
          <p class="az-text-small text-color-500 mb-0">
            Crie um template de task e vincule ao evento <strong>Lead ganho no CRM</strong>
            em Tarefas &rsaquo; Configurações &rsaquo; Triggers.
            Assim, toda vez que um lead for ganho, esta sugestão aparece preenchida automaticamente.
          </p>
        </div>
        <div v-else class="text-center py-4">
          <icon-svg name="mdi-lightning-bolt-outline" :size="48" color="var(--secondary)" class="mb-3" />
          <p class="az-text-body text-color-700 mb-2">
            Nenhum template de task configurado para este evento.
          </p>
          <p class="az-text-small text-color-500 mb-0">
            Fale com o gestor do escritório para configurar templates de tasks automáticas
            ao ganhar leads no CRM.
          </p>
        </div>
      </div>

      <!-- Picker step (N templates) -->
      <div v-else-if="step === 'picker'">
        <p class="az-text-small text-color-600 mb-4">
          Existem múltiplos templates configurados para este evento.
          Escolha qual deseja usar:
        </p>

        <v-radio-group v-model="selectedMappingId" hide-details class="mt-0 pt-0">
          <v-radio
            v-for="opt in pickerOptions"
            :key="opt.id"
            :value="opt.id"
            :label="opt.title"
            class="mb-2"
          />
        </v-radio-group>
      </div>

      <!-- Wizard step (1 template selecionado) -->
      <div v-else>
        <div class="mb-4">
          <span class="az-text-label text-color-700 mb-1 d-block">Título da task</span>
          <InputByType
            v-model="form.title"
            type="text"
            placeholder="Ex.: Onboarding João Silva"
            dense
            outlined
          />
        </div>

        <v-row dense>
          <v-col cols="12" md="6">
            <span class="az-text-label text-color-700 mb-1 d-block">Responsável principal</span>
            <TaskAssigneePicker
              :value="form.responsavelPrincipal"
              mode="single"
              placeholder="Selecionar responsável"
              @input="onResponsavelInput"
            />
          </v-col>
          <v-col cols="12" md="6">
            <span class="az-text-label text-color-700 mb-1 d-block">Participantes</span>
            <TaskAssigneePicker
              :value="form.participantes"
              mode="multi"
              placeholder="Selecionar participantes"
              @input="onParticipantesInput"
            />
          </v-col>
        </v-row>

        <div class="mt-4">
          <span class="az-text-label text-color-700 mb-1 d-block">Cliente</span>
          <TaskClientPicker
            :value="form.cliente"
            placeholder="Vincular cliente"
            @input="onClienteInput"
          />
        </div>

        <v-row dense class="mt-4">
          <v-col cols="12" md="6">
            <span class="az-text-label text-color-700 mb-1 d-block">Data base</span>
            <InputByType
              v-model="form.baseDate"
              type="date"
              placeholder="Selecionar data base"
              dense
              outlined
            />
          </v-col>
          <v-col cols="12" md="3">
            <span class="az-text-label text-color-700 mb-1 d-block">Pasta</span>
            <v-select
              v-model="form.folderId"
              :items="folderOptions"
              dense
              outlined
              hide-details
              clearable
              placeholder="Pasta"
              :loading="loadingFolders"
              :disabled="loadingFolders"
              :menu-props="{ offsetY: true }"
            />
          </v-col>
          <v-col cols="12" md="3">
            <span class="az-text-label text-color-700 mb-1 d-block">Sprint</span>
            <v-select
              v-model="form.sprintId"
              :items="sprintOptions"
              dense
              outlined
              hide-details
              clearable
              :disabled="loadingFolders || !form.folderId"
              placeholder="Sprint"
              :menu-props="{ offsetY: true }"
            />
          </v-col>
        </v-row>
      </div>
    </div>

    <template v-slot:actions>
      <div class="d-flex align-center mt-4 justify-end">
        <BaseButton underlined @click="dismiss">
          {{ step === 'empty' ? 'Fechar' : 'Dispensar' }}
        </BaseButton>
        <BaseButton
          v-if="step === 'empty' && isGestor"
          primary
          @click="goToTriggerSettings"
        >
          Configurar templates
        </BaseButton>
        <BaseButton
          v-else-if="step === 'picker'"
          primary
          :disabled="!canContinueFromPicker"
          @click="confirmPicker"
        >
          Continuar
        </BaseButton>
        <BaseButton
          v-else-if="step === 'wizard'"
          primary
          :loading="loading"
          :disabled="!canSubmit"
          @click="submit"
        >
          Criar task
        </BaseButton>
      </div>
    </template>
  </BaseModal>
</template>
